/**
 * Syntax highlighting for chat fenced blocks (highlight.js, GitHub-dark–style tokens).
 */

import hljs from 'highlight.js/lib/core';
import bash from 'highlight.js/lib/languages/bash';
import cpp from 'highlight.js/lib/languages/cpp';
import csharp from 'highlight.js/lib/languages/csharp';
import css from 'highlight.js/lib/languages/css';
import diff from 'highlight.js/lib/languages/diff';
import go from 'highlight.js/lib/languages/go';
import java from 'highlight.js/lib/languages/java';
import javascript from 'highlight.js/lib/languages/javascript';
import json from 'highlight.js/lib/languages/json';
import kotlin from 'highlight.js/lib/languages/kotlin';
import markdown from 'highlight.js/lib/languages/markdown';
import php from 'highlight.js/lib/languages/php';
import plaintext from 'highlight.js/lib/languages/plaintext';
import python from 'highlight.js/lib/languages/python';
import ruby from 'highlight.js/lib/languages/ruby';
import rust from 'highlight.js/lib/languages/rust';
import scss from 'highlight.js/lib/languages/scss';
import sql from 'highlight.js/lib/languages/sql';
import swift from 'highlight.js/lib/languages/swift';
import typescript from 'highlight.js/lib/languages/typescript';
import xml from 'highlight.js/lib/languages/xml';
import yaml from 'highlight.js/lib/languages/yaml';

let registered = false;

function ensureRegistered(): void {
  if (registered) return;
  registered = true;
  hljs.registerLanguage('xml', xml);
  hljs.registerLanguage('javascript', javascript);
  hljs.registerLanguage('typescript', typescript);
  hljs.registerLanguage('css', css);
  hljs.registerLanguage('scss', scss);
  hljs.registerLanguage('json', json);
  hljs.registerLanguage('bash', bash);
  hljs.registerLanguage('python', python);
  hljs.registerLanguage('markdown', markdown);
  hljs.registerLanguage('java', java);
  hljs.registerLanguage('cpp', cpp);
  hljs.registerLanguage('csharp', csharp);
  hljs.registerLanguage('go', go);
  hljs.registerLanguage('rust', rust);
  hljs.registerLanguage('sql', sql);
  hljs.registerLanguage('yaml', yaml);
  hljs.registerLanguage('php', php);
  hljs.registerLanguage('ruby', ruby);
  hljs.registerLanguage('swift', swift);
  hljs.registerLanguage('kotlin', kotlin);
  hljs.registerLanguage('diff', diff);
  hljs.registerLanguage('plaintext', plaintext);
}

const LANG_ALIASES: Record<string, string> = {
  html: 'xml',
  htm: 'xml',
  vue: 'xml',
  svg: 'xml',
  js: 'javascript',
  jsx: 'javascript',
  mjs: 'javascript',
  cjs: 'javascript',
  ts: 'typescript',
  tsx: 'typescript',
  py: 'python',
  rb: 'ruby',
  sh: 'bash',
  zsh: 'bash',
  shell: 'bash',
  console: 'bash',
  yml: 'yaml',
  cs: 'csharp',
  hpp: 'cpp',
  h: 'cpp',
  jsonc: 'json',
  md: 'markdown',
  text: 'plaintext',
  txt: 'plaintext'
};

const AUTO_LANGS = [
  'javascript',
  'typescript',
  'xml',
  'css',
  'json',
  'python',
  'java',
  'bash',
  'markdown',
  'cpp',
  'csharp',
  'go',
  'rust',
  'sql',
  'yaml',
  'php'
];

function normalizeLang(raw: string): string {
  const k = String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/[^\w+-]/g, '');
  if (!k) return 'plaintext';
  return LANG_ALIASES[k] || k;
}

function toBase64Utf8(s: string): string {
  try {
    return btoa(unescape(encodeURIComponent(s)));
  } catch {
    return '';
  }
}

function highlightInner(lang: string, code: string): string {
  ensureRegistered();
  const lg = normalizeLang(lang);
  try {
    if (hljs.getLanguage(lg)) {
      return hljs.highlight(code, { language: lg, ignoreIllegals: true }).value;
    }
  } catch {
    /* fall through */
  }
  try {
    const r = hljs.highlightAuto(code, AUTO_LANGS);
    return r.value;
  } catch {
    try {
      return hljs.highlight(code, { language: 'plaintext', ignoreIllegals: true }).value;
    } catch {
      return code.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }
  }
}

const COPY_SVG =
  '<svg class="rf-chat-md-copy-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>';
const CHECK_SVG =
  '<svg class="rf-chat-md-copy-icon rf-chat-md-copy-check" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>';

/**
 * Full HTML for one fenced code block: toolbar (lang + copy) + highlighted code.
 */
export function renderChatCodeBlock(langLabel: string, rawCode: string): string {
  const lang = String(langLabel || '').trim().slice(0, 24) || 'code';
  const displayLang = escapeAttr(lang);
  const b64 = toBase64Utf8(rawCode);
  const inner = highlightInner(lang, rawCode);
  return (
    '<div class="rf-chat-md-code-wrap">' +
    '<div class="rf-chat-md-code-toolbar">' +
    '<span class="rf-chat-md-code-lang">' +
    displayLang +
    '</span>' +
    '<button type="button" class="rf-chat-md-copy" data-code-b64="' +
    escapeAttr(b64) +
    '" title="Copy code" aria-label="Copy code">' +
    COPY_SVG +
    CHECK_SVG +
    '</button>' +
    '</div>' +
    '<div class="rf-chat-md-pre-outer">' +
    '<pre class="rf-chat-md-pre"><code class="hljs language-' +
    escapeAttr(normalizeLang(lang)) +
    '">' +
    inner +
    '</code></pre>' +
    '</div>' +
    '</div>'
  );
}

function escapeAttr(s: string): string {
  return String(s || '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/'/g, '&#39;');
}
